import React, { useMemo } from 'react';
import { TimeCondition, VALID_DAYS } from '../../../../types/qr.types';

interface TimezoneOption {
  value: string;
  label: string;
}

// Get formatted timezone label using Intl API
function getTimezoneLabel(tz: string): string {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'shortOffset',
    });
    const parts = formatter.formatToParts(now);
    const offsetPart = parts.find(p => p.type === 'timeZoneName');
    const offset = offsetPart?.value || '';

    // Format: "America/New_York" -> "New York (GMT-5)"
    const cityName = tz.split('/').pop()?.replace(/_/g, ' ') || tz;
    return `${cityName} (${offset})`;
  } catch {
    return tz;
  }
}

// Get region from timezone (e.g., "America/New_York" -> "America")
function getTimezoneRegion(tz: string): string {
  if (tz === 'UTC') return 'UTC';
  const region = tz.split('/')[0];
  // Normalize region names for display
  const regionMap: Record<string, string> = {
    'America': 'Americas',
    'Europe': 'Europe',
    'Asia': 'Asia',
    'Africa': 'Africa',
    'Australia': 'Australia & Pacific',
    'Pacific': 'Australia & Pacific',
    'Atlantic': 'Atlantic',
    'Indian': 'Indian Ocean',
    'Antarctica': 'Antarctica',
  };
  return regionMap[region] || region;
}

// Comprehensive fallback list of common timezones
const FALLBACK_TIMEZONES = [
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Anchorage', 'America/Phoenix', 'America/Toronto', 'America/Vancouver',
  'America/Mexico_City', 'America/Sao_Paulo', 'America/Buenos_Aires',
  'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Madrid', 'Europe/Rome',
  'Europe/Amsterdam', 'Europe/Brussels', 'Europe/Vienna', 'Europe/Warsaw',
  'Europe/Moscow', 'Europe/Istanbul',
  'Asia/Tokyo', 'Asia/Shanghai', 'Asia/Hong_Kong', 'Asia/Singapore', 'Asia/Seoul',
  'Asia/Bangkok', 'Asia/Jakarta', 'Asia/Kolkata', 'Asia/Dubai', 'Asia/Riyadh',
  'Australia/Sydney', 'Australia/Melbourne', 'Australia/Perth',
  'Pacific/Auckland', 'Pacific/Honolulu',
  'Africa/Cairo', 'Africa/Johannesburg', 'Africa/Lagos', 'Africa/Nairobi',
];

// Build timezone list - tries native API first, falls back to comprehensive list
function getGroupedTimezones(): Record<string, TimezoneOption[]> {
  const grouped: Record<string, TimezoneOption[]> = {};

  // Add UTC first
  grouped['UTC'] = [{ value: 'UTC', label: 'UTC (Coordinated Universal Time)' }];

  // Try to get timezones from native API, fall back to our list
  let timezones: string[] = FALLBACK_TIMEZONES;

  try {
    // Intl.supportedValuesOf is available in modern browsers
    const intlAny = Intl as { supportedValuesOf?: (key: string) => string[] };
    if (typeof intlAny.supportedValuesOf === 'function') {
      timezones = intlAny.supportedValuesOf('timeZone');
    }
  } catch {
    // Use fallback list
  }

  for (const tz of timezones) {
    const region = getTimezoneRegion(tz);
    const label = getTimezoneLabel(tz);

    if (!grouped[region]) {
      grouped[region] = [];
    }
    grouped[region].push({ value: tz, label });
  }

  // Sort timezones within each group alphabetically
  Object.keys(grouped).forEach(region => {
    grouped[region].sort((a: TimezoneOption, b: TimezoneOption) =>
      a.label.localeCompare(b.label)
    );
  });

  return grouped;
}

// Preferred region order for display
const REGION_ORDER = ['UTC', 'Americas', 'Europe', 'Asia', 'Australia & Pacific', 'Africa', 'Atlantic', 'Indian Ocean', 'Antarctica'];

interface TimeConditionEditorProps {
  condition: TimeCondition;
  onChange: (condition: TimeCondition) => void;
  onRemove: () => void;
}

export function TimeConditionEditor({ condition, onChange, onRemove }: TimeConditionEditorProps) {
  // Memoize grouped timezones to avoid recalculating on every render
  const groupedTimezones = useMemo(() => getGroupedTimezones(), []);

  // Get sorted regions based on preferred order
  const sortedRegions = useMemo(() => {
    const regions = Object.keys(groupedTimezones);
    return regions.sort((a, b) => {
      const aIndex = REGION_ORDER.indexOf(a);
      const bIndex = REGION_ORDER.indexOf(b);
      if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });
  }, [groupedTimezones]);

  const handleDayToggle = (day: string) => {
    const newDays = condition.days.includes(day)
      ? condition.days.filter(d => d !== day)
      : [...condition.days, day];
    onChange({ ...condition, days: newDays });
  };

  return (
    <div className="bg-[--clinqr-bg-subtle]/50 border border-[--clinqr-border]/10 rounded-lg p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-[--clinqr-primary]">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </span>
          <span className="font-medium text-[--clinqr-text-primary]">Time-Based</span>
        </div>
        <button
          type="button"
          onClick={onRemove}
          className="text-red-500 hover:text-red-700 p-1"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
          <input
            type="time"
            value={condition.start_time}
            onChange={(e) => onChange({ ...condition, start_time: e.target.value })}
            className="w-full h-10 px-3 rounded-lg border border-[--clinqr-border]/20 focus:outline-none focus:border-[--clinqr-primary] bg-white text-[--clinqr-text-primary]"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[--clinqr-text-secondary] mb-1">End Time</label>
          <input
            type="time"
            value={condition.end_time}
            onChange={(e) => onChange({ ...condition, end_time: e.target.value })}
            className="w-full h-10 px-3 rounded-lg border border-[--clinqr-border]/20 focus:outline-none focus:border-[--clinqr-primary] bg-white text-[--clinqr-text-primary]"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-[--clinqr-text-secondary] mb-2">Active Days</label>
        <div className="flex flex-wrap gap-2">
          {VALID_DAYS.map((day) => (
            <button
              key={day}
              type="button"
              onClick={() => handleDayToggle(day)}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                condition.days.includes(day)
                  ? 'bg-[--clinqr-lime] text-[--clinqr-navy] border border-[--clinqr-navy]/20'
                  : 'bg-[--clinqr-bg-subtle] text-[--clinqr-text-secondary] border border-[--clinqr-border]/10 hover:bg-gray-200'
              }`}
            >
              {day.slice(0, 3).charAt(0).toUpperCase() + day.slice(1, 3)}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-[--clinqr-text-secondary] mb-1">Timezone</label>
        <select
          value={condition.timezone}
          onChange={(e) => onChange({ ...condition, timezone: e.target.value })}
          className="w-full h-10 px-3 rounded-lg border border-[--clinqr-border]/20 focus:outline-none focus:border-[--clinqr-primary] bg-white text-[--clinqr-text-primary]"
        >
          {sortedRegions.map(region => (
            <optgroup key={region} label={region}>
              {groupedTimezones[region]?.map(tz => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>
    </div>
  );
}
